import { TrashOutlined } from 'assets/icons/revamp';
import ActivityInstructionImage from 'components/activity-image/ActivityInstructionImage';
import LoadingIndicator from 'components/common/LoadingIndicator';
import BaseButton from 'components/ui/BaseButton';
import Container from 'components/ui/Container';
import COLOR from 'constants/color';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { ImageStatus } from './Images';
import ImageError from './ImageError';
import ImagePlaceholder from './ImagePlaceholder';
import { useActivityInstructionImagesDraftContext } from 'contexts/ActivityInstructionImagesDraftContext';
import { moreOptionModalImagesSectionSelector } from 'store/reducer/modal/more-options.modal.selectors';
import UploadFileButton from 'components/file-uploader/UploadFileButton';
import { ActivityImageUploadResult } from 'services/image-upload';
import { isMoreOptionActivityPersisted } from 'utils/more-options.util';
import { MAXIMUM_IMAGE_URLS } from 'constants/general';
import { ACTIVITY_IMAGE_STATUS } from 'constants/image';

const ImagesList = ({
  setImageStatuses,
  imageStatuses
}: {
  imageStatuses: Record<string, ImageStatus>;
  setImageStatuses: Dispatch<SetStateAction<Record<string, ImageStatus>>>;
}) => {
  const dispatch = useAppDispatch();
  const deletingRef = useRef(false);
  const {
    removeAtDraftIndex,
    draftImageUrls,
    getDraftRowKey,
    isDraftAtMax,
    addDraftFile
  } = useActivityInstructionImagesDraftContext();
  const { activity, type, previousSettings, previousActivityLibrary } =
    useAppSelectorShallow(moreOptionModalImagesSectionSelector);
  const isMaxReached = isDraftAtMax(MAXIMUM_IMAGE_URLS);
  const isPersisted = useMemo(
    () =>
      isMoreOptionActivityPersisted({
        activity,
        type,
        previousSettings,
        previousActivityLibrary
      }),
    [activity, previousActivityLibrary, previousSettings, type]
  );

  const activityId = activity?.id ?? '';

  const addedImages = useMemo(() => {
    return (draftImageUrls || []).map((img, index) => ({
      rowKey: getDraftRowKey(index),
      mergedIndex: index,
      url: typeof img === 'string' ? img : img.url || '',
      filePath: typeof img === 'string' ? undefined : img.file_path
    }));
  }, [draftImageUrls, getDraftRowKey]);

  useEffect(() => {
    setImageStatuses((prev) => {
      const newStatuses: Record<string, ImageStatus> = { ...prev };
      const activeKeys = new Set<string>();

      addedImages.forEach((img) => {
        activeKeys.add(img.rowKey);
        if ((img.url?.trim() || img.filePath) && !newStatuses[img.rowKey]) {
          newStatuses[img.rowKey] = ACTIVITY_IMAGE_STATUS.LOADING;
        }
      });

      Object.keys(newStatuses).forEach((key) => {
        if (!activeKeys.has(key)) {
          delete newStatuses[key];
        }
      });

      return newStatuses;
    });
  }, [addedImages, setImageStatuses]);

  const handleImageLoad = (rowKey: string) => {
    setImageStatuses((prev) => ({
      ...prev,
      [rowKey]: ACTIVITY_IMAGE_STATUS.LOADED
    }));
  };

  const handleImageError = (rowKey: string) => {
    setImageStatuses((prev) => ({
      ...prev,
      [rowKey]: ACTIVITY_IMAGE_STATUS.ERROR
    }));
  };

  const handleDeleteImage = (mergedIndex: number, rowKey: string) => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setImageStatuses((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    removeAtDraftIndex(mergedIndex, dispatch);
    deletingRef.current = false;
  };

  return (
    <Container className='~space-y-1/2'>
      {addedImages.length > 0 && (
        <div className='flex flex-col ~gap-2/3'>
          <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
            {t('tabs_revamp.instructions_tab.count_images_added', {
              count: addedImages.length
            })}
          </p>
        </div>
      )}
      <div className='flex flex-wrap items-center ~gap-2/3'>
        {addedImages.map((image) => {
          const status =
            imageStatuses[image.rowKey] || ACTIVITY_IMAGE_STATUS.LOADING;
          const isLoading = status === ACTIVITY_IMAGE_STATUS.LOADING;
          const isError = status === ACTIVITY_IMAGE_STATUS.ERROR;

          return (
            <div
              key={image.rowKey}
              className='relative ~w-20/24 ~h-20/24 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden group'>
              {isLoading && (
                <div className='absolute inset-0 flex items-center justify-center bg-gray-200/80 dark:bg-gray-700/80 z-20 pointer-events-none'>
                  <LoadingIndicator dims='~w-6/8 ~h-6/8' />
                </div>
              )}
              {!isError && (image.url?.trim() || image.filePath) && (
                <ActivityInstructionImage
                  activityId={activityId}
                  url={image.url}
                  filePath={image.filePath}
                  alt='Activity instruction'
                  imgClassName={`w-full h-full object-cover transition-opacity duration-300 ${
                    isLoading ? 'opacity-0' : 'opacity-100'
                  }`}
                  onLoad={() => handleImageLoad(image.rowKey)}
                  onError={() => handleImageError(image.rowKey)}
                />
              )}
              {isError && <ImageError />}

              {!image.url?.trim() &&
                !image.filePath &&
                !isLoading &&
                !isError && <ImagePlaceholder />}
              <BaseButton
                onClick={() =>
                  handleDeleteImage(image.mergedIndex, image.rowKey)
                }
                aria-label={t('tabs_revamp.instructions_tab.delete_image')}
                icon={
                  <TrashOutlined
                    className='~w-3/4 ~h-3/4'
                    fill={COLOR.SEMANTIC.ERROR}
                  />
                }
                size='xs'
                additionalStyles='absolute -bottom-0.5 -right-0.5 z-30 !rounded-tr-none !rounded-br-none !px-1 !py-1 hover:bg-primary-50/60 dark:hover:bg-primary-50/50'
                variant='icon'
              />
            </div>
          );
        })}
        {!isMaxReached && isPersisted && (
          <UploadFileButton
            setUrls={(files: ActivityImageUploadResult[]) => {
              if (files.length) {
                addDraftFile(files[0].fileKey, dispatch);
              }
            }}
            activityId={activity?.id ?? ''}
            variant='tile'
          />
        )}
      </div>
    </Container>
  );
};

export default ImagesList;
